import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, ArrowLeft } from 'lucide-react';
import { GridBackground } from '../components/TechUI';
import SEO from '../components/SEO';

const PrivacyPolicyPage = () => {
    const lastUpdated = 'February 26, 2026';

    return (
        <GridBackground className="min-h-screen">
            <SEO
                title="Privacy Policy | Rastor AI"
                description="Privacy Policy for Rastor AI — how we collect, use, store, and protect your personal information."
            />
            <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <Link
                    to="/"
                    className="inline-flex items-center text-slate-400 hover:text-emerald-400 transition-colors font-mono text-xs uppercase tracking-wider mb-8"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
                </Link>

                <div className="flex items-center space-x-4 mb-8">
                    <div className="w-12 h-12 border border-emerald-500/50 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                        <Lock className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-white font-mono">PRIVACY POLICY</h1>
                        <p className="text-slate-500 text-xs font-mono uppercase mt-1">Last Updated: {lastUpdated}</p>
                    </div>
                </div>

                <div className="bg-[#0F131F]/60 border border-white/5 rounded-2xl p-8 md:p-10 space-y-8 backdrop-blur-sm">
                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">1. Introduction</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono">
                            Rastor AI ("Company", "we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <Link to="/" className="text-emerald-400 hover:underline">www.rastorai.com</Link> and use our platform and services (collectively, the "Service"). Please read this policy carefully. By using the Service, you consent to the practices described in this policy.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">2. Information We Collect</h2>

                        <h3 className="text-sm font-bold text-white font-mono mt-4 mb-2">2.1 Personal Information You Provide</h3>
                        <ul className="list-disc list-inside text-slate-300 text-sm font-mono space-y-1 pl-4">
                            <li>Full name and email address (during account registration)</li>
                            <li>Phone number (optional, for payment processing)</li>
                            <li>Professional details: current role, experience, skills, education, industry</li>
                            <li>Resume data (when using our resume builder or analysis features)</li>
                            <li>Career goals, preferences, and assessment responses</li>
                            <li>Organization/university affiliation (if applicable)</li>
                            <li>Payment information (processed securely via Cashfree; we do not store card details)</li>
                        </ul>

                        <h3 className="text-sm font-bold text-white font-mono mt-4 mb-2">2.2 Information Collected Automatically</h3>
                        <ul className="list-disc list-inside text-slate-300 text-sm font-mono space-y-1 pl-4">
                            <li>Device information (browser type, operating system, device type)</li>
                            <li>IP address and approximate geographic location</li>
                            <li>Usage data (pages visited, features used, time spent, click patterns)</li>
                            <li>Cookies and similar tracking technologies</li>
                            <li>Referral source and campaign tracking data</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">3. How We Use Your Information</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono mb-3">
                            We use the information we collect for the following purposes:
                        </p>
                        <ul className="list-disc list-inside text-slate-300 text-sm font-mono space-y-1 pl-4">
                            <li>To provide, operate, and maintain the Service</li>
                            <li>To generate personalized AI career roadmaps, assessments, and recommendations</li>
                            <li>To process subscriptions, payments, and transactions</li>
                            <li>To send transactional emails (verification, receipts, subscription updates)</li>
                            <li>To send marketing communications and newsletters (with your consent; you may opt out at any time)</li>
                            <li>To improve and personalize the user experience</li>
                            <li>To monitor usage patterns and analyze trends for product improvement</li>
                            <li>To detect and prevent fraud, abuse, and security threats</li>
                            <li>To comply with legal obligations</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">4. Data Sharing & Disclosure</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono mb-3">
                            We do not sell your personal information. We may share your data with:
                        </p>
                        <ul className="list-disc list-inside text-slate-300 text-sm font-mono space-y-1 pl-4">
                            <li><strong className="text-white">Service Providers:</strong> Firebase (authentication & database), Cashfree (payments), Resend (transactional emails), Render (hosting)</li>
                            <li><strong className="text-white">Analytics Partners:</strong> Anonymized, aggregated data for product analytics</li>
                            <li><strong className="text-white">B2B/Enterprise Organizations:</strong> If you join via an organization code, limited profile and progress data may be shared with your organization administrator</li>
                            <li><strong className="text-white">Legal Compliance:</strong> When required by law, regulation, or legal process</li>
                            <li><strong className="text-white">Business Transfers:</strong> In connection with any merger, acquisition, or sale of assets</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">5. Data Security</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono">
                            We implement industry-standard security measures to protect your information, including 256-bit SSL/TLS encryption for data in transit, secure cloud infrastructure via Google Firebase and Render, and access controls for our internal systems. However, no method of electronic transmission or storage is 100% secure, and we cannot guarantee absolute security.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">6. Data Retention</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono">
                            We retain your personal information for as long as your account is active or as needed to provide the Service. If you delete your account, we will delete or anonymize your personal data within 30 days, except where retention is required by law (e.g., transaction records for tax/accounting purposes, which may be retained for up to 7 years).
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">7. Cookies & Tracking</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono">
                            We use cookies and similar technologies (localStorage, session storage) to maintain your login session, remember preferences, and improve your experience. You can control cookie settings through your browser. Disabling cookies may affect some features of the Service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">8. Your Rights</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono mb-3">
                            You have the following rights regarding your personal data:
                        </p>
                        <ul className="list-disc list-inside text-slate-300 text-sm font-mono space-y-1 pl-4">
                            <li><strong className="text-white">Access:</strong> Request a copy of the personal data we hold about you</li>
                            <li><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete data</li>
                            <li><strong className="text-white">Deletion:</strong> Request deletion of your personal data</li>
                            <li><strong className="text-white">Portability:</strong> Request your data in a structured, machine-readable format</li>
                            <li><strong className="text-white">Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
                        </ul>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono mt-3">
                            To exercise any of these rights, contact us at <a href="mailto:support@rastorai.com" className="text-emerald-400 hover:underline">support@rastorai.com</a>. We will respond within 30 days.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">9. Children's Privacy</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono">
                            The Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we learn that we have collected information from a child under 13, we will promptly delete it.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">10. Third-Party Links</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono">
                            The Service may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any personal information.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">11. Changes to This Policy</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono">
                            We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on this page and updating the "Last Updated" date. Your continued use constitutes acceptance of the revised policy.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">12. Contact Us</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono">
                            For privacy-related inquiries:
                        </p>
                        <div className="mt-3 bg-white/5 border border-white/10 rounded-xl p-4 font-mono text-sm">
                            <p className="text-white font-bold">Rastor AI — Data Protection</p>
                            <p className="text-slate-400">Email: <a href="mailto:support@rastorai.com" className="text-emerald-400 hover:underline">support@rastorai.com</a></p>
                            <p className="text-slate-400">Phone: <a href="tel:+918130829610" className="text-emerald-400 hover:underline">+91 8130829610</a></p>
                            <p className="text-slate-400">Website: <Link to="/" className="text-emerald-400 hover:underline">www.rastorai.com</Link></p>
                        </div>
                    </section>
                </div>
            </div>
        </GridBackground>
    );
};

export default PrivacyPolicyPage;
